/**
 * The button look, as Tailwind utilities.
 *
 * Shared with the link component and with the raw <a> elements scattered
 * through the pages, which is why it lives in its own module rather than
 * inside the Button component — those anchors used to reach the same rules
 * through the `.link--button*` class names in button/style.css.
 */

export type ButtonVariant =
  'primary' | 'ghost-primary' | 'secondary' | 'destructive' | 'confirm';

export type ButtonSize = 'default' | 'small';

/**
 * The focus ring, for light page backgrounds — white, bone, purple-100.
 *
 * `outline-offset-4` puts the ring on the page background rather than on the
 * component, so the contrast that matters is ring-against-page.
 *
 * The width matters more than it looks. Tailwind's `outline-<color>` utility
 * emits `outline-color` and nothing else, so on its own it never draws
 * anything — the ring you saw was the UA's `:focus-visible` default, and both
 * Chromium and WebKit paint that with `outline-style: auto`, which ignores the
 * author's outline-color entirely. The colour token was decorative until this
 * width was added, which is why swapping it for a compliant value had to come
 * with `outline-2`.
 *
 * `focus-visible` rather than `focus`: the ring is for people navigating by
 * keyboard. On plain `focus` it also fired on every mouse click, which is the
 * noise most sites strip out with `outline: none` and then never put back.
 */
const FOCUS_RING_SHAPE =
  'focus-visible:outline-2 focus-visible:outline-offset-4';

export const FOCUS_RING = `${FOCUS_RING_SHAPE} focus-visible:outline-focus-state-primary`;

/**
 * The focus ring for dark surfaces — the purple-500 and blue heroes.
 *
 * This is an alternative to FOCUS_RING, never an addition. The two colour
 * utilities have identical specificity, so an element carrying both is
 * resolved by their order in the stylesheet, not by the order they appear in
 * the class attribute — and the raw <a> elements in the pages do not go
 * through twMerge() to have the loser stripped. Compose with
 * BUTTON_CHROME rather than BUTTON_BASE so only one is ever present.
 */
export const FOCUS_RING_INVERSE = `${FOCUS_RING_SHAPE} focus-visible:outline-focus-state-inverse`;

/**
 * The dark-surface ring applied from a wrapper, for subtrees whose links are
 * rendered by something else — the footer's Gatsby <Link>s, the nav on a
 * purple-500 background.
 *
 * Safe to combine with FOCUS_RING on the descendants: the descendant selector
 * adds an element to the specificity, so this wins on the elements it covers.
 */
export const FOCUS_RING_INVERSE_CONTAINER = [
  '[&_a]:focus-visible:outline-2',
  '[&_a]:focus-visible:outline-offset-4',
  '[&_a]:focus-visible:outline-focus-state-inverse',
  '[&_button]:focus-visible:outline-2',
  '[&_button]:focus-visible:outline-offset-4',
  '[&_button]:focus-visible:outline-focus-state-inverse',
].join(' ');

/**
 * Re-asserts the light-surface ring on a light panel nested inside a dark
 * container — the mobile navigation sheet, which is bone but lives under the
 * purple-500 nav bar and would otherwise inherit a bone ring on bone.
 *
 * Marked important deliberately. This and FOCUS_RING_INVERSE_CONTAINER produce
 * selectors of identical specificity, so without it the winner would be
 * whichever Tailwind happened to emit last — true today, but not something the
 * nesting should depend on.
 */
export const FOCUS_RING_PRIMARY_CONTAINER = [
  '[&_a]:focus-visible:outline-focus-state-primary!',
  '[&_button]:focus-visible:outline-focus-state-primary!',
].join(' ');

/**
 * Chrome common to every button and button-shaped link.
 *
 * `ease-[ease]` is deliberate: the original transition used the CSS keyword
 * `ease`, not Tailwind's default cubic-bezier(0.4, 0, 0.2, 1).
 *
 * `active:` deliberately does not touch the outline. It used to remove it
 * entirely, which blanked the focus ring for exactly as long as a keyboard
 * user held Space or Enter — the whole time they were pressing the button.
 *
 * (Spelling that removal out here would be self-defeating: Tailwind scans
 * comments too, and would emit the utility for a class nothing uses.)
 */
export const BUTTON_CHROME = [
  'inline-flex items-center justify-center',
  'min-h-11 overflow-visible px-6 py-1.5',
  'rounded-md border-2 border-transparent',
  'font-[inherit] text-base font-semibold tracking-[0.02em]',
  'cursor-pointer appearance-none select-none',
  'origin-center transition-[background-color,transform] duration-200 ease-[ease]',
  'active:scale-[0.99]',
].join(' ');

/** BUTTON_CHROME with the light-surface ring. The default for everything. */
export const BUTTON_BASE = `${BUTTON_CHROME} ${FOCUS_RING}`;

export const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-button-primary text-type-white hover:bg-button-primary-hover hover:text-type-white active:bg-button-primary-active active:text-type-white',
  'ghost-primary':
    'border-button-primary bg-transparent text-button-primary hover:bg-button-primary-hover hover:text-type-white active:bg-button-primary-active active:text-type-white',
  secondary:
    'border-button-secondary bg-transparent text-button-secondary hover:border-button-secondary-hover hover:bg-button-secondary-hover hover:text-type-white active:border-button-secondary-active active:bg-button-secondary-active active:text-type-white',
  destructive:
    'bg-button-destructive text-type-white hover:bg-button-destructive-hover active:bg-button-destructive-active',
  confirm:
    'bg-button-confirm text-type-white hover:bg-button-confirm-hover active:bg-button-confirm-active',
};

export const BUTTON_SIZE: Record<ButtonSize, string> = {
  default: 'min-h-11',
  small: 'min-h-9',
};

export const BUTTON_BLOCK = 'h-12 w-full';

export const BUTTON_DISABLED = 'pointer-events-none opacity-40';
